import { ReactNode, RefObject } from 'react';
import { Loader2, ArrowDown } from 'lucide-react';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';

interface PullToRefreshContainerProps {
  onRefresh: () => Promise<void>;
  scrollContainerRef: RefObject<HTMLElement | null>;
  children: ReactNode;
}

export function PullToRefreshContainer({
  onRefresh,
  scrollContainerRef,
  children,
}: PullToRefreshContainerProps) {
  const { pullDistance, isRefreshing, state } = usePullToRefresh(
    scrollContainerRef,
    onRefresh,
  );

  const threshold = 60;
  const progress = Math.min(pullDistance / threshold, 1);
  const pastThreshold = pullDistance >= threshold;
  const isActive = state !== 'idle';

  return (
    <div className="relative">
      {/* Pull indicator */}
      <div
        className="absolute left-0 right-0 flex items-center justify-center z-10 pointer-events-none"
        style={{
          top: 0,
          height: `${pullDistance}px`,
          opacity: isActive ? Math.min(progress * 1.5, 1) : 0,
        }}
      >
        <div className="flex items-center justify-center w-10 h-10">
          {isRefreshing ? (
            <Loader2 className="w-6 h-6 text-[#8bc43e] animate-spin" />
          ) : (
            <ArrowDown
              className="w-6 h-6 text-[#8bc43e]"
              style={{
                transform: `rotate(${pastThreshold ? 180 : 0}deg)`,
                transition: 'transform 0.2s ease',
              }}
            />
          )}
        </div>
      </div>

      {/* Content wrapper */}
      <div
        style={{
          transform: `translateY(${pullDistance}px)`,
          transition: state === 'idle' ? 'transform 0.3s ease' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}
